import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Send, Clock, FileText, Brain, CheckSquare, BookOpen, Paperclip, X, Eye, Sparkles, BarChart3, User, ClipboardCheck, RotateCcw, Check, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import TaskCreationModal from '../components/Tasks/TaskCreationModal';
import AITaskGenerator from '../components/Tasks/AITaskGenerator';
import FileViewer from '../components/Tasks/FileViewer';
import AnalyticsDrawer from '../components/AnalyticsDrawer';
import ManualGradingModal from '../components/Tasks/ManualGradingModal';
import TaskDetailModal from '../components/Tasks/TaskDetailModal';
import AdminTaskPreviewModal from '../components/Tasks/AdminTaskPreviewModal';
import CombinedTaskReviewModal from '../components/Tasks/CombinedTaskReviewModal';
import TasksAnalyticsDrawer from '../components/Analytics/TasksAnalyticsDrawer';
import api from '../utils/api';


const getEffectiveStatus = (task) => {
    if (!task || !task.status) return 'draft';

    const { status, scheduled_date } = task;

    // Published tasks
    if (status === 'published') return 'published';

    // Scheduled tasks
    if (status === 'scheduled') {
        if (scheduled_date) {
            const scheduledTime = new Date(scheduled_date).getTime();
            const now = Date.now();
            if (scheduledTime <= now) return 'published'; // Scheduled time passed
            return 'scheduled'; // Future scheduled
        }
        // If scheduled_date is null, treat as published (already live)
        return 'published';
    }

    // Draft tasks
    return 'draft';
};



const AdminTasksPage = () => {
    const [activeTab, setActiveTab] = useState('onboarding'); // 'onboarding' or 'preboarding'
    const [tasks, setTasks] = useState([]);
    const [pendingReviewTasks, setPendingReviewTasks] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [filter, setFilter] = useState('all'); // all, active, pending_review, completed, templates, scheduled, draft
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    // File Viewing State
    const [viewingTaskAttachments, setViewingTaskAttachments] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);

    // Page Level Analytics State
    const [isPageAnalyticsOpen, setIsPageAnalyticsOpen] = useState(false);

    // AI Task Generator State
    const [isAIGeneratorOpen, setIsAIGeneratorOpen] = useState(false);
    const [aiEditTask, setAiEditTask] = useState(null);

    // Analytics Drawer State
    const [drawerContext, setDrawerContext] = useState(null);

    // Manual Grading State
    const [isGradingModalOpen, setIsGradingModalOpen] = useState(false);
    const [selectedGradingSubmission, setSelectedGradingSubmission] = useState(null);

    // View Task Modal State
    const [viewingTask, setViewingTask] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    // Preview Modal State
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewTask, setPreviewTask] = useState(null);

    // Review Modal State
    const [reviewModalData, setReviewModalData] = useState(null);

    useEffect(() => {
        fetchTasks();
        fetchPendingReviewTasks(); // Always fetch pending review tasks for the dedicated section
    }, [filter, activeTab]);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = { phase: activeTab };

            // Map filter values to backend status values
            if (filter === 'active') {
                params.status = 'published';
            } else if (filter === 'completed') {
                params.status = 'completed';
            } else if (filter === 'scheduled') {
                params.status = 'scheduled';
            } else if (filter === 'draft') {
                params.status = 'draft';
            }

            const response = await api.get('/tasks', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setTasks(response.data.tasks || []);
        } catch (error) {
            console.error('Error fetching tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingReviewTasks = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = {};

            const response = await api.get('/tasks/pending-review', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setPendingReviewTasks(response.data.tasks || []);
        } catch (error) {
            console.error('Error fetching pending review tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTask = () => {
        setSelectedTask(null);
        setIsModalOpen(true);
    };

    const handleEditTask = (task) => {
        // Check if this is an AI-generated task
        if (task.is_ai_generated) {
            // Open AI Task Generator with the task data for editing
            setAiEditTask(task);
            setIsAIGeneratorOpen(true);
        } else {
            // Open regular task creation modal for non-AI tasks
            setSelectedTask(task);
            setIsModalOpen(true);
        }
    };

    const handleViewTask = (task) => {
        setViewingTask(task);
        setIsViewModalOpen(true);
    };

    const handlePreviewTask = (task) => {
        setPreviewTask(task);
        setIsPreviewModalOpen(true);
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;

        try {
            const token = localStorage.getItem('token');
            await api.delete(`/tasks/${taskId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchTasks();
        } catch (error) {
            console.error('Error deleting task:', error);
            alert('Failed to delete task');
        }
    };

    const handlePublishTask = async (taskId) => {
        try {
            const token = localStorage.getItem('token');
            await api.post(`/tasks/${taskId}/publish`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchTasks();
            alert('Task published successfully!');
        } catch (error) {
            console.error('Error publishing task:', error);
            alert('Failed to publish task');
        }
    };

    const handleViewStats = (task) => {
        setDrawerContext({ type: 'task', id: task.id, title: task.title });
    };

    const handleGradeSubmission = (submission) => {
        setSelectedGradingSubmission(submission);
        setIsGradingModalOpen(true);
    };

    const handleApproveRetake = async (submission) => {
        if (!window.confirm('Are you sure you want to approve this retake request? The employee will be able to take the quiz again.')) {
            return;
        }

        const loadingToast = toast.loading('Approving retake request...');
        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/tasks/${submission.id}/review`,
                { action: 'resubmit' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Retake request approved!', { id: loadingToast });
            fetchPendingReviewTasks(); // Refresh the list
        } catch (error) {
            console.error('Error approving retake:', error);
            toast.error('Failed to approve retake request', { id: loadingToast });
        }
    };

    const handleRejectRetake = async (submission) => {
        if (!window.confirm('Are you sure you want to reject this retake request? The task status will revert to its previous state (Completed or Failed).')) {
            return;
        }

        const loadingToast = toast.loading('Rejecting retake request...');
        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/tasks/${submission.id}/review`,
                { action: 'reject_retake' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Retake request rejected', { id: loadingToast });
            fetchPendingReviewTasks();
        } catch (error) {
            console.error('Error rejecting retake:', error);
            toast.error('Failed to reject retake request', { id: loadingToast });
        }
    };

    // Handle proof approval
    const handleApproveProof = async (submission) => {
        const loadingToast = toast.loading('Approving submission...');
        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/tasks/${submission.id}/review`,
                { action: 'approve' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Submission approved!', { id: loadingToast });
            fetchPendingReviewTasks();
        } catch (error) {
            console.error('Error approving submission:', error);
            toast.error('Failed to approve submission', { id: loadingToast });
        }
    };

    // Handle proof rejection
    const handleRejectProof = async (submission) => {
        const feedback = window.prompt('Please provide feedback for the rejection (optional):');
        if (feedback === null) return; // User cancelled

        const loadingToast = toast.loading('Rejecting submission...');
        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/tasks/${submission.id}/review`,
                { action: 'resubmit', feedback: feedback || 'Please resubmit with correct proof of completion.' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Submission sent back for resubmission', { id: loadingToast });
            fetchPendingReviewTasks();
        } catch (error) {
            console.error('Error rejecting submission:', error);
            toast.error('Failed to reject submission', { id: loadingToast });
        }
    };

    const handleViewSubmission = (submission) => {
        const task = {
            id: submission.task_id,
            title: submission.task_title || submission.title,
            description: submission.task_description,
            sections: submission.sections,
            is_combined: true,
            task_id: submission.task_id
        };
        const assignment = {
            ...submission,
            status: submission.status,
            section_progress: submission.section_progress,
            current_section_index: 0,
            employee_name: `${submission.first_name} ${submission.last_name}`
        };

        setReviewModalData({ task, assignment });
    };

    const getTaskIcon = (taskType) => {
        switch (taskType) {
            case 'quiz':
                return <Brain className="w-5 h-5" />;
            case 'training':
                return <BookOpen className="w-5 h-5" />;
            case 'checklist':
                return <CheckSquare className="w-5 h-5" />;
            default:
                return <FileText className="w-5 h-5" />;
        }
    };

    const getStatusBadge = (task) => {
        const status = getEffectiveStatus(task);
        const styles = {
            draft: 'bg-muted text-muted-foreground',
            scheduled: 'bg-orange-100 text-orange-700',
            published: 'bg-green-100 text-green-700'
        };

        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.draft}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };



    const filteredTasks = tasks.filter(task =>
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground mb-2">Activity Centre — Tasks</h1>
                <p className="text-muted-foreground text-sm">Manage tasks for employees across different phases</p>
            </div>

            {/* Tabs */}
            <div className="mb-6">
                <div className="border-b-2 border-border">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => {
                                setActiveTab('onboarding');
                                setFilter('all');
                                setSearchQuery('');
                            }}
                            className={`
                                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                                ${activeTab === 'onboarding'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                }
                            `}
                        >
                            Onboarding Tasks
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('preboarding');
                                setFilter('all');
                                setSearchQuery('');
                            }}
                            className={`
                                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                                ${activeTab === 'preboarding'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                }
                            `}
                        >
                            Preboarding Tasks
                        </button>
                    </nav>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-card border border-border rounded-xl p-3 mb-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="relative flex-1 min-w-[250px] max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search tasks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 h-9 border border-input rounded-lg text-sm bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="h-9 border border-input rounded-lg px-3 text-sm bg-background text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                        >
                            <option value="all">All Tasks</option>
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="draft">Drafts</option>
                        </select>
                        <button
                            onClick={() => setIsPageAnalyticsOpen(true)}
                            className="flex items-center gap-2 h-9 px-3.5 text-sm font-medium bg-card text-primary border border-primary/20 rounded-lg hover:bg-primary/5 transition-colors shadow-sm"
                        >
                            <BarChart3 className="w-4 h-4" />
                            View Insights
                        </button>
                        <button
                            onClick={() => { setAiEditTask(null); localStorage.removeItem('ai_task_draft_session'); setIsAIGeneratorOpen(true); }}
                            className="flex items-center gap-2 h-9 px-3.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Create Task
                        </button>
                    </div>
                </div>
            </div>

            {/* Tasks Card List */}
            <div style={{ marginBottom: '24px' }}>
                {loading ? (
                    <div className="p-12 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-4 text-muted-foreground text-sm">Loading tasks...</p>
                    </div>
                ) : filteredTasks.length === 0 ? (
                    <div className="p-12 text-center bg-card border border-border rounded-xl">
                        <FileText className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                        <h3 className="text-base font-semibold text-foreground mb-2">No tasks found</h3>
                        <p className="text-muted-foreground text-sm mb-4">Get started by creating your first {activeTab} task</p>
                        <button
                            onClick={() => { setAiEditTask(null); localStorage.removeItem('ai_task_draft_session'); setIsAIGeneratorOpen(true); }}
                            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2 rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Create Task
                        </button>
                    </div>
                ) : (
                    filteredTasks.map((task) => {
                        const isCompliance = task.is_compliance_required;
                        const effectiveStatus = getEffectiveStatus(task);
                        const statusColors = {
                            draft: { bg: 'bg-muted', text: 'text-muted-foreground' },
                            scheduled: { bg: 'bg-orange-100', text: 'text-orange-700' },
                            published: { bg: 'bg-green-100', text: 'text-green-700' }
                        };
                        const sc = statusColors[effectiveStatus] || statusColors.draft;

                        return (
                            <div
                                key={task.id}
                                onClick={() => handleViewTask(task)}
                                className="bg-card border border-border rounded-xl p-4 mb-3 cursor-pointer transition-all hover:border-primary hover:shadow-sm"
                            >
                                {/* Row 1: Icon + Title + Badges */}
                                <div className="flex items-start gap-3 mb-2.5">
                                    <div className={`mt-0.5 flex-shrink-0 ${isCompliance ? 'text-blue-600' : 'text-primary'}`}>
                                        {isCompliance ? <Shield className="w-[18px] h-[18px]" /> : getTaskIcon(task.task_type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-foreground">{task.title}</span>
                                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${sc.bg} ${sc.text}`}>
                                                {effectiveStatus.charAt(0).toUpperCase() + effectiveStatus.slice(1)}
                                            </span>
                                            {isCompliance && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-white bg-blue-600 px-1.5 py-0.5 rounded">
                                                    <Shield className="w-2.5 h-2.5" /> Compliance
                                                </span>
                                            )}
                                            {task.attachments && task.attachments.length > 0 && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setViewingTaskAttachments(task); }}
                                                    className="inline-flex items-center gap-1 text-[11px] text-primary bg-primary/10 px-1.5 py-0.5 rounded border-none cursor-pointer hover:bg-primary/20 transition-colors"
                                                >
                                                    <Paperclip className="w-3 h-3" /> {task.attachments.length}
                                                </button>
                                            )}
                                        </div>
                                        {task.created_via_course_id && (
                                            <div className="inline-flex items-center gap-1 mt-1 text-[11px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">
                                                <BookOpen className="w-3 h-3" /> Course: {task.course_name}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Row 2: Metadata */}
                                <div className="flex items-center gap-4 text-[13px] text-muted-foreground mb-2.5 pl-[30px] flex-wrap">
                                    <span className="capitalize">{task.task_type || 'task'}</span>
                                    <span className="text-border">•</span>
                                    <span className="flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                                        {task.completed_count || 0} completed
                                    </span>
                                    <span className="text-border">•</span>
                                    <span>{task.assigned_count || 0} assigned</span>
                                </div>

                                {/* Row 3: Actions */}
                                <div className="flex items-center justify-end gap-2 pl-[30px] flex-wrap">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handlePreviewTask(task); }}
                                        className="h-8 px-3 text-xs font-medium text-muted-foreground bg-secondary hover:bg-muted rounded-lg flex items-center gap-1.5 transition-colors"
                                    >
                                        <Eye className="w-3.5 h-3.5" /> Preview
                                    </button>
                                    {(task.status === 'published' || effectiveStatus === 'published') && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleViewStats(task); }}
                                            className="h-8 px-3 text-xs font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg flex items-center gap-1.5 transition-colors"
                                        >
                                            <BarChart3 className="w-3.5 h-3.5" /> Stats
                                        </button>
                                    )}
                                    {task.status === 'draft' && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handlePublishTask(task.id); }}
                                            className="h-8 px-3 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg flex items-center gap-1.5 transition-colors"
                                        >
                                            <Send className="w-3.5 h-3.5" /> Publish
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                                        className="h-8 px-3 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg flex items-center gap-1.5 transition-colors"
                                    >
                                        <Edit className="w-3.5 h-3.5" /> Edit
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                                        className="h-8 px-3 text-xs font-medium text-destructive bg-destructive/10 hover:bg-destructive/20 rounded-lg flex items-center gap-1.5 transition-colors"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Pending Review Section */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="border-b border-border px-4 py-3.5 bg-gradient-to-r from-yellow-50/50 to-orange-50/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-yellow-100 text-yellow-700">
                            <ClipboardCheck className="w-4.5 h-4.5" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-foreground">Pending Review</h3>
                            <p className="text-sm text-muted-foreground">
                                {pendingReviewTasks.length} submission{pendingReviewTasks.length !== 1 ? 's' : ''} awaiting your review
                            </p>
                        </div>
                    </div>
                </div>

                {pendingReviewTasks.length === 0 ? (
                    <div className="p-8 text-center">
                        <ClipboardCheck className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                        <h4 className="text-foreground font-medium mb-1">All caught up!</h4>
                        <p className="text-sm text-muted-foreground">No submissions pending review</p>
                    </div>
                ) : (
                    <div className="p-4 space-y-3">
                        {pendingReviewTasks.map((submission) => (
                            <div
                                key={submission.id}
                                className="border border-border rounded-lg p-4 hover:border-yellow-400 hover:shadow-sm transition-all bg-card"
                            >
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex items-start gap-4 flex-1 min-w-0">
                                        <div className="p-3 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                                            {getTaskIcon(submission.task_type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-semibold text-foreground mb-1 truncate">
                                                {submission.title}
                                            </h4>
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2 flex-wrap">
                                                <div className="flex items-center gap-1">
                                                    <User className="w-4 h-4" />
                                                    <span>{submission.first_name} {submission.last_name}</span>
                                                </div>
                                                {submission.position && (
                                                    <>
                                                        <span className="text-border">•</span>
                                                        <span>{submission.position}</span>
                                                    </>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <span className="px-2 py-1 rounded text-xs font-medium bg-secondary text-secondary-foreground capitalize">
                                                    {submission.task_type === 'combined' || submission.is_combined ? 'Combined Task' : submission.task_type}
                                                </span>
                                                {submission.status === 'retake_requested' && (
                                                    <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700 flex items-center gap-1">
                                                        <RotateCcw className="w-3 h-3" />
                                                        Retake Requested
                                                    </span>
                                                )}
                                                {submission.quiz_grading_type === 'manual' && submission.status !== 'retake_requested' && (
                                                    <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                                                        Manual Grading
                                                    </span>
                                                )}
                                                <span className="px-2 py-1 rounded text-xs font-medium bg-muted text-muted-foreground">
                                                    Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
                                                </span>
                                                {submission.score !== null && submission.score !== undefined && (
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${submission.score >= (submission.passing_score || 70)
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-700'
                                                        }`}>
                                                        Score: {submission.score}%
                                                    </span>
                                                )}
                                            </div>

                                            {/* Section Breakdown for Combined Tasks */}
                                            {(submission.is_combined || submission.task_type === 'combined') && submission.sections && (
                                                <div className="mt-3 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                                                    <p className="text-xs font-semibold text-blue-800 mb-2">Section Progress:</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {submission.sections.map((section) => {
                                                            const sectionProgress = submission.section_progress?.[section.id];
                                                            const isCompleted = sectionProgress?.status === 'completed';
                                                            const sectionScore = sectionProgress?.score;

                                                            return (
                                                                <div
                                                                    key={section.id}
                                                                    className={`flex items-center gap-2 px-3 py-1.5 rounded border text-xs ${isCompleted
                                                                        ? 'bg-green-50 border-green-200 text-green-700'
                                                                        : 'bg-background border-border text-muted-foreground'
                                                                        }`}
                                                                >
                                                                    {section.section_type === 'quiz' && <Brain className="w-3 h-3" />}
                                                                    {section.section_type === 'training' && <BookOpen className="w-3 h-3" />}
                                                                    {section.section_type === 'checklist' && <CheckSquare className="w-3 h-3" />}
                                                                    {section.section_type === 'content' && <FileText className="w-3 h-3" />}
                                                                    <span className="font-medium">{section.title}</span>
                                                                    {isCompleted && <Check className="w-3 h-3" />}
                                                                    {sectionScore !== null && sectionScore !== undefined && (
                                                                        <span className="font-bold">({sectionScore}%)</span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Proof Files Display */}
                                            {(submission.proof_files || submission.proof_url) && (
                                                <div className="mt-3 p-3 bg-muted/50 rounded-lg border border-border">
                                                    <p className="text-xs font-semibold text-muted-foreground mb-2">Submitted Proof:</p>
                                                    {submission.proof_files && (() => {
                                                        const files = typeof submission.proof_files === 'string'
                                                            ? JSON.parse(submission.proof_files)
                                                            : submission.proof_files;
                                                        return files.length > 0 && (
                                                            <div className="flex flex-wrap gap-2 mb-2">
                                                                {files.map((file, idx) => (
                                                                    <button
                                                                        key={idx}
                                                                        onClick={() => setPreviewFile(file)}
                                                                        className="flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded text-sm hover:border-primary hover:bg-primary/5 transition-colors"
                                                                    >
                                                                        <FileText className="w-4 h-4 text-muted-foreground" />
                                                                        <span className="text-muted-foreground truncate max-w-[150px]">{file.file_name}</span>
                                                                        <Eye className="w-3 h-3 text-muted-foreground" />
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                    {submission.proof_url && (
                                                        <a
                                                            href={submission.proof_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-sm text-primary hover:underline flex items-center gap-1"
                                                        >
                                                            <Eye className="w-3 h-3" />
                                                            View proof link
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                            {/* Signature Display */}
                                            {submission.signature_data && (
                                                <div className="mt-3 p-3 bg-muted/50 rounded-lg border border-border">
                                                    <p className="text-xs font-semibold text-muted-foreground mb-2">Signature:</p>
                                                    <div className="border border-border rounded bg-background p-2 inline-block">
                                                        {(submission.signature_data.startsWith('http') || submission.signature_data.startsWith('data:')) ? (
                                                            <button
                                                                onClick={() => setPreviewFile({
                                                                    file_name: 'Signature',
                                                                    file_type: 'image/png',
                                                                    file_url: submission.signature_data
                                                                })}
                                                                className="group relative"
                                                                title="Click to view full screen"
                                                            >
                                                                <img
                                                                    src={submission.signature_data}
                                                                    alt="Signature"
                                                                    className="max-h-16 max-w-full object-contain group-hover:opacity-90 transition-opacity"
                                                                />
                                                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 rounded">
                                                                    <Eye className="w-4 h-4 text-gray-700" />
                                                                </div>
                                                            </button>
                                                        ) : (
                                                            <p className="text-lg font-signature text-foreground px-2 py-1">
                                                                {submission.signature_data}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {(submission.is_combined || submission.task_type === 'combined') && (
                                            <button
                                                onClick={() => handleViewSubmission(submission)}
                                                className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg font-medium transition-colors text-sm"
                                                title="View Submission Details"
                                            >
                                                <Eye className="w-4 h-4" />
                                                Review Content
                                            </button>
                                        )}
                                        {submission.status === 'retake_requested' ? (
                                            <>
                                                <button
                                                    onClick={() => handleRejectRetake(submission)}
                                                    className="flex items-center gap-2 px-3 py-2 text-destructive bg-destructive/10 hover:bg-destructive/20 rounded-lg font-medium transition-colors text-sm"
                                                    title="Reject Retake Request"
                                                >
                                                    <X className="w-4 h-4" />
                                                    Reject
                                                </button>
                                                <button
                                                    onClick={() => handleApproveRetake(submission)}
                                                    className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors text-sm"
                                                >
                                                    <Check className="w-4 h-4" />
                                                    Approve Retake
                                                </button>
                                            </>
                                        ) : submission.task_type === 'quiz' && submission.quiz_grading_type === 'manual' ? (
                                            <button
                                                onClick={() => handleGradeSubmission(submission)}
                                                className="flex items-center gap-2 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors text-sm"
                                            >
                                                <ClipboardCheck className="w-4 h-4" />
                                                Grade Quiz
                                            </button>
                                        ) : (
                                            /* Proof submission - show approve/reject */
                                            <>
                                                <button
                                                    onClick={() => handleRejectProof(submission)}
                                                    className="flex items-center gap-2 px-3 py-2 text-destructive bg-destructive/10 hover:bg-destructive/20 rounded-lg font-medium transition-colors text-sm"
                                                >
                                                    <X className="w-4 h-4" />
                                                    Request Resubmission
                                                </button>
                                                <button
                                                    onClick={() => handleApproveProof(submission)}
                                                    className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors text-sm"
                                                >
                                                    <Check className="w-4 h-4" />
                                                    Approve
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* View Task Detail Modal */}
            <TaskDetailModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                task={viewingTask}
                isAdmin={true}
                onEdit={(task) => {
                    setIsViewModalOpen(false);
                    handleEditTask(task);
                }}
            />

            {/* Preview Task Modal — Employee-style view */}
            <AdminTaskPreviewModal
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
                task={previewTask}
            />

            {/* Task Creation Modal */}
            {
                isModalOpen && (
                    <TaskCreationModal
                        isOpen={isModalOpen}
                        onClose={() => {
                            setIsModalOpen(false);
                            setSelectedTask(null);
                        }}
                        onSuccess={() => {
                            fetchTasks();
                            setIsModalOpen(false);
                            setSelectedTask(null);
                        }}
                        task={selectedTask}
                        phase={activeTab}
                    />
                )
            }

            {/* Attachments List Modal */}
            {
                viewingTaskAttachments && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4">
                        <div className="bg-card rounded-none md:rounded-2xl shadow-lg w-full max-w-md h-full md:h-auto overflow-hidden flex flex-col">
                            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
                                <h3 className="font-semibold text-foreground">
                                    Attachments ({viewingTaskAttachments.attachments.length})
                                </h3>
                                <button
                                    onClick={() => setViewingTaskAttachments(null)}
                                    className="p-1 text-muted-foreground hover:bg-secondary rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-2 flex-1 overflow-y-auto">
                                {viewingTaskAttachments.attachments.map((file, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setPreviewFile(file)}
                                        className="w-full text-left flex items-center gap-3 p-3 hover:bg-secondary rounded-lg group transition-colors"
                                    >
                                        <div className="p-2 bg-secondary rounded-lg text-muted-foreground">
                                            <FileText className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-foreground truncate text-sm">
                                                {file.file_name}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                Click to preview
                                            </p>
                                        </div>
                                        <Eye className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Application-level File Viewer */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />

            {/* AI Task Generator */}
            <AITaskGenerator
                isOpen={isAIGeneratorOpen}
                onClose={() => {
                    setIsAIGeneratorOpen(false);
                    setAiEditTask(null);
                }}
                onSuccess={() => {
                    fetchTasks();
                    setIsAIGeneratorOpen(false);
                    setAiEditTask(null);
                }}
                phase={activeTab}
                editTask={aiEditTask}
                initialMessages={aiEditTask?.ai_chat_history}
            />

            {/* Analytics Drawer */}
            <AnalyticsDrawer
                isOpen={!!drawerContext}
                onClose={() => setDrawerContext(null)}
                context={drawerContext}
            />

            {/* Manual Grading Modal */}
            <ManualGradingModal
                isOpen={isGradingModalOpen}
                onClose={() => {
                    setIsGradingModalOpen(false);
                    setSelectedGradingSubmission(null);
                }}
                submission={selectedGradingSubmission}
                onSuccess={() => {
                    fetchPendingReviewTasks();
                    setIsGradingModalOpen(false);
                    setSelectedGradingSubmission(null);
                }}
            />

            {/* Combined Task Review Modal */}
            <CombinedTaskReviewModal
                isOpen={!!reviewModalData}
                onClose={() => setReviewModalData(null)}
                task={reviewModalData?.task}
                assignment={reviewModalData?.assignment}
            />
            {/* Page Level Analytics Drawer */}
            <TasksAnalyticsDrawer
                isOpen={isPageAnalyticsOpen}
                onClose={() => setIsPageAnalyticsOpen(false)}
            />
        </div >
    );
};

export default AdminTasksPage;

